/**
 * Typed fetch client for `/api/v1` (ARCHITECTURE.md §7).
 *
 * Every call carries the session cookie (`credentials: "include"`, AUTHZ.md).
 * Failures are normalised into `ApiError { status, code, message, details }`.
 *
 * Endpoints that belong to the annotator / adjudicator / admin screens are
 * declared here as thin stubs with their request/response types, so the agents
 * that build those features only fill in call sites, not the transport.
 */
import type { AiAdminStatus, AiPublicResult, MergeResult } from "@xinghai/shared";
import type {
  Assignment,
  AssignmentState,
  Consent,
  Domain,
  Elicitation,
  Material,
  PerceptionTrial,
  Recording,
  RecordingQc,
  Role,
  Speaker,
  Task,
  TrialType,
} from "@xinghai/shared";
import type {
  AnnotationAssignPayload,
  AnnotationUnassignPayload,
  BatchCreatePayload,
  BatchDispatchPayload,
  ExportFreezePayload,
  ExportPreviewPayload,
  JudgmentSubmitPayload,
  MaterialImportPayload,
  RecordingQcPayload,
  RetestCreatePayload,
  SpeakerImportPayload,
  SpeakerUpdatePayload,
} from "@xinghai/shared";
// Type-only (erased at build time) — the expert screens own these shapes.
import type {
  AnnotateDetail,
  AnnotateQueueParams,
  AnnotateQueueResponse,
  AnnotateSubmitResponse,
  ArbitrationDecidePayload,
  ArbitrationDecideResponse,
  ArbitrationDetail,
  ArbitrationQueueResponse,
  GoldenPayload,
  GoldenResponse,
  QualityAnnotatorsResponse,
  QualityWeeklyResponse,
  RereviewConfirmResponse,
  RereviewResponse,
} from "../features/annotator/types";
// Type-only (erased at build time) — the 课程团队 / 管理端 screens own these.
import type {
  AdminUser,
  AdminUserListResponse,
  AdminUserResponse,
  AnnotatorTrack,
  AuditListResponse,
  ClassItem,
  ClassListResponse,
  ClassMemberListResponse,
  ClassResponse,
  Invitation as AdminInvitation,
  InvitationCreateResponse,
  InvitationListResponse,
  ResetLinkResponse,
  BatchDetailResponse,
  BatchDispatchResponse,
  BatchListResponse,
  BatchResponse,
  ExportFreezeResponse,
  ExportListResponse,
  ExportPreviewResponse,
  MaterialDetailResponse,
  MaterialFreezeResponse,
  MaterialImportResponse,
  MaterialListResponse,
  QcBulkResponse,
  QcDecisionResponse,
  QcListResponse,
  TeacherDashboardResponse,
  BatchStudentsResponse,
  SpeakerImportResponse,
  SpeakerListResponse,
  SpeakerUpdateResponse,
  StimuliListResponse,
  StimulusUploadResponse,
  AssignAnnotatorsResponse,
  RetestResponse,
  UnassignAnnotatorsResponse,
  BackupListResponse,
  BackupRunResponse,
  CoverageAxis,
  CoverageResponse,
  DeepHealthResponse,
} from "../features/admin/types";

export const API_BASE = "/api/v1";

// --------------------------------------------------------------------------- //
// errors
// --------------------------------------------------------------------------- //

export interface ApiErrorBody {
  code: string;
  message: string;
  details?: unknown;
}

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly details: unknown;

  constructor(status: number, body: ApiErrorBody) {
    super(body.message);
    this.name = "ApiError";
    this.status = status;
    this.code = body.code;
    this.details = body.details;
  }

  /** 401 — the session expired or was never established. */
  get isUnauthorized(): boolean {
    return this.status === 401;
  }

  /** 403 + `CONSENT_REQUIRED` — 红线 14.6 三授权前置. */
  get isConsentRequired(): boolean {
    return this.status === 403 && this.code === "CONSENT_REQUIRED";
  }

  /** Network / offline failures surface with status 0. */
  get isNetwork(): boolean {
    return this.status === 0;
  }
}

const FALLBACK_MESSAGE: Record<number, string> = {
  0: "网络连接不可用，请检查网络后重试。",
  400: "提交的内容有误，请检查后重试。",
  401: "登录状态已过期，请重新登录。",
  403: "你没有执行该操作的权限。",
  404: "没有找到对应的内容。",
  409: "该操作和当前状态冲突，请刷新后重试。",
  413: "文件太大，无法上传。",
  422: "提交的内容有误，请检查后重试。",
  500: "服务出了点问题，请稍后重试。",
};

/** Normalise any error-ish payload into `{code,message,details}`. */
export function toErrorBody(status: number, payload: unknown): ApiErrorBody {
  const fallback = FALLBACK_MESSAGE[status] ?? "请求失败，请稍后重试。";
  if (payload && typeof payload === "object") {
    const raw = payload as Record<string, unknown>;
    const nested =
      raw.error && typeof raw.error === "object" ? (raw.error as Record<string, unknown>) : raw;
    const code = typeof nested.code === "string" ? nested.code : `HTTP_${status}`;
    const message =
      typeof nested.message === "string" && nested.message.trim().length > 0
        ? nested.message
        : fallback;
    return { code, message, details: nested.details };
  }
  return { code: `HTTP_${status}`, message: fallback };
}

// --------------------------------------------------------------------------- //
// transport
// --------------------------------------------------------------------------- //

interface RequestOptions {
  method?: "GET" | "POST" | "PATCH" | "DELETE";
  body?: unknown;
  query?: Record<string, string | number | boolean | undefined | null>;
  signal?: AbortSignal;
}

function withQuery(path: string, query: RequestOptions["query"]): string {
  if (!query) return path;
  const qs = new URLSearchParams();
  for (const [k, v] of Object.entries(query)) {
    if (v === undefined || v === null || v === "") continue;
    qs.set(k, String(v));
  }
  const s = qs.toString();
  return s ? `${path}?${s}` : path;
}

export async function apiFetch<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  const url = `${API_BASE}${withQuery(path, opts.query)}`;
  let res: Response;
  try {
    res = await fetch(url, {
      method: opts.method ?? "GET",
      credentials: "include",
      headers:
        opts.body === undefined
          ? { Accept: "application/json" }
          : {
              Accept: "application/json",
              "Content-Type": "application/json",
            },
      body: opts.body === undefined ? undefined : JSON.stringify(opts.body),
      ...(opts.signal ? { signal: opts.signal } : {}),
    });
  } catch (cause) {
    throw new ApiError(
      0,
      toErrorBody(0, { code: "NETWORK", message: FALLBACK_MESSAGE[0], details: String(cause) }),
    );
  }

  const text = await res.text();
  let payload: unknown = null;
  if (text.length > 0) {
    try {
      payload = JSON.parse(text);
    } catch {
      payload = null;
    }
  }

  if (!res.ok) throw new ApiError(res.status, toErrorBody(res.status, payload));
  return payload as T;
}

// --------------------------------------------------------------------------- //
// shared response shapes
// --------------------------------------------------------------------------- //

export interface SessionUser {
  id: string;
  username: string;
  display_name: string;
  roles: Role[];
  disabled?: boolean;
  /** `shared.ts meResponse()` sends this on the user, not at the top level. */
  must_change_password?: boolean;
  /** ACCOUNTS.md §5 标注者方向 (null for non-annotators). */
  annotator_track?: string | null;
  work_agreement_version?: string | null;
  work_agreement_at?: string | null;
}

/**
 * `GET auth/me` — as the pages consume it.
 *
 * The server (`routes/shared.ts meResponse()`) sends `password_change_required`
 * at the top level and the work agreement as two flat `user.*` columns;
 * {@link adaptMe} normalises both so no page has to know that.
 */
export interface MeResponse {
  user: SessionUser;
  /** ACCOUNTS.md §3 — every route except /account/password is blocked while true. */
  must_change_password?: boolean;
  /** Server field name, kept so a page may read either. */
  password_change_required?: boolean;
  /** Present for staff who signed 《标注成果授权协议》. */
  work_agreement?: { version: string; accepted_at?: string | null } | null;
  /** present when the user has the `student` role */
  speaker: Speaker | null;
  /** 红线 14.6 — true until all three consents (and guardian, if minor) are signed */
  consent_required: boolean;
  is_minor: boolean;
  /** Class ids this account teaches (师生回避 / 教师视图). */
  teaches_class_ids?: string[];
  profile_incomplete?: boolean;
  birth_year_required?: boolean;
  memberships?: { class_id: string; class_name: string; role: Role }[];
  recovery?: { email_verified: boolean; phone_verified: boolean; smtp: boolean; sms: boolean };
  /** True when this student already has at least one take. */
  has_recordings?: boolean;
  /**
   * True only for a brand-new student. Missing means the client should keep
   * the local onboarding flag (older fixtures). `false` skips 试录.
   */
  warmup_required?: boolean;
  /** Present only while an admin is viewing a student. The student themselves never gets it. */
  viewing_as?: {
    user_id: string;
    display_name: string;
    started_at: string;
    expires_at: string;
  };
}

/** Raw `meResponse()` body → {@link MeResponse}. Tolerates already-adapted input. */
export function adaptMe(raw: MeResponse): MeResponse {
  const user = raw.user ?? ({} as SessionUser);
  const version = user.work_agreement_version ?? null;
  return {
    ...raw,
    must_change_password:
      raw.must_change_password ??
      raw.password_change_required ??
      user.must_change_password ??
      false,
    work_agreement:
      raw.work_agreement ??
      (version ? { version, accepted_at: user.work_agreement_at ?? null } : null),
  };
}

async function fetchMe(path: string, opts: RequestOptions = {}): Promise<MeResponse> {
  return adaptMe(await apiFetch<MeResponse>(path, opts));
}

/** Material plus the T7 stimulus bundle (only present for `kind: "perception"`). */
export interface PerceptionItem {
  id: string;
  trial_type: TrialType;
  /** absolute or app-relative audio URLs; 2 for AX, 3 for ABX, 1 for ID */
  stimuli: string[];
  /** choice labels for ID trials (pinyin), omitted for AX/ABX */
  options?: string[];
  /** expected answer: AX `"same"|"different"`, ABX `"A"|"B"`, ID one of `options` */
  answer: string | null;
  confusion_class?: string | null;
  prompt?: string | null;
}

export interface PerceptionBundle {
  items: PerceptionItem[];
}

export interface MaterialWithPerception extends Material {
  perception?: PerceptionBundle | null;
  /** optional coaching line shown above the material (「注意平翘舌」) */
  hint?: string | null;
  /** T4 passage: the sentence split the student records one by one */
  sentences?: string[] | null;
  /**
   * Per-material recording cap. Absent means the §9 default of 22 000 ms;
   * anything longer marks a 气息 (breath) item that needs one long take.
   */
  max_duration_ms?: number | null;
}

/** One row of `GET me/assignments` — assignment + everything the record page needs. */
export interface AssignmentItem extends Assignment {
  domain: Domain;
  material: MaterialWithPerception;
  batch_title?: string | null;
  /** estimated minutes for the whole assignment */
  eta_minutes?: number | null;
  /** uploads already accepted (T3 = 2 passes, T4 = one per sentence) */
  recorded_count?: number;
  /** uploads expected before the assignment counts as done */
  required_count?: number;
  /** T5↔T6: 1 = 朗读, 2 = 演唱 */
  pair_step?: number | null;
  /** server-decided: 演唱 stays locked until the 朗读 pass is recorded */
  locked?: boolean;
  /**
   * docs/UX.md §1.3 — for a T6 演唱 assignment: the student's own T5
   * 朗读 recording, auto-played as a reference. Absent until that pass exists.
   */
  paired_audio_url?: string | null;
  locked_reason?: string | null;
}

export interface AssignmentsResponse {
  consent_required: boolean;
  assignments: AssignmentItem[];
}

export interface RecordingListItem extends Recording {
  material_text?: string | null;
  material_title?: string | null;
  /** Locked pinyin for the material, space-separated; shown with tone marks. */
  material_pinyin?: string | null;
  created_at?: string | null;
  qc: RecordingQc | null;
  /** Present only when AI v1 is enabled and this caller may see the score. */
  ai?: AiPublicResult | null;
}

export interface RecordingsResponse {
  recordings: RecordingListItem[];
}

/** One expert's judgment on `GET speakers/:id/recordings`. */
export interface ExpertJudgmentView {
  display_name: string;
  track: string | null;
  submitted_at: string;
  utterance: Record<string, number | string | null>;
  chars: Array<{ char_index: number; mark_type: string | null; note?: string | null }>;
  listening_effort: number | null;
  accentedness: number | null;
  note: string | null;
}

export interface ExpertReview {
  hidden_reason?: "pending_own_review";
  judgments?: ExpertJudgmentView[];
  merge?: {
    result: MergeResult;
    needs_arbitration: boolean;
    merged_at: string;
  } | null;
  arbitration?: Array<{
    char_index: number | null;
    dimension: string;
    decision: string;
    value: number | string | null;
    note: string | null;
    decided_at: string;
  }>;
}

export interface SpeakerRecordingsResponse {
  speaker: { id: string; display_name: string; class_id: string | null };
  recordings: Array<RecordingListItem & { expert: ExpertReview }>;
}

/** Extra per-upload context the server stores verbatim on the recording. */
export interface ClientMeta {
  /** T3 绕口令: which of the two passes this upload is */
  pass?: "slow" | "normal";
  /** T4 段落: zero-based index of the sentence */
  segment_index?: number;
  clipping: boolean;
  clip_count: number;
  silence_ratio: number;
  peak: number;
  /** §9 环境检测: RMS of the 2 s room-noise probe taken at session start. */
  noise_floor_rms?: number;
  /** §9: 20·log10(speech RMS / noise floor) for this take. */
  snr_db?: number;
  /** the student submitted despite a clipping / silence warning */
  forced?: boolean;
  user_agent?: string;
}

export interface UploadRecordingInput {
  assignment_id: string;
  session_id: string;
  elicitation?: Elicitation;
  duration_ms: number;
  sample_rate: number;
  blob: Blob;
  filename?: string;
  client_meta: ClientMeta;
  paired_with?: string | null;
  retest_of?: string | null;
  /**
   * docs/ROADMAP.md B2 — idempotency key minted once per take and reused by
   * every retry of the offline queue. The server dedupes on
   * `(assignment_id, upload_key)`, so a补传 can never create a second recording.
   */
  upload_key?: string | null;
}

export interface UploadRecordingResponse {
  recording: Recording;
  /** B2 — `true` when this POST resolved to a recording an earlier attempt made. */
  deduplicated?: boolean;
}

// --------------------------------------------------------------------------- //
// auth
// --------------------------------------------------------------------------- //

/** `POST auth/register` — 班级邀请码自助注册 (docs/ROADMAP.md A1). */
export interface RegisterPayload {
  invite_code: string;
  /** 3–32 chars, `[a-z0-9_]`. */
  username: string;
  /** ≥10 chars (docs/ROADMAP.md B7). */
  password: string;
  display_name: string;
  /** Optional at signup; finished later on 完善资料. */
  dialect_l1?: string[];
  hometown?: string;
  training_level?: "vocal" | "non_vocal" | "freshman";
  birth_year?: number | null;
  gender?: "F" | "M" | "other" | null;
  contact?: string;
  consent?: { train: boolean; display: boolean; commercial: boolean };
}

/** `GET|PATCH me/profile` — the parts of the background survey a student may edit. */
export interface ProfilePayload {
  dialect_l1: string[];
  hometown: string | null;
  training_level: "vocal" | "non_vocal" | "freshman" | string;
  contact: string | null;
}

export interface ProfileResponse {
  profile: ProfilePayload;
}

/** `GET auth/setup` — whether the first-admin page is still open. */
/** GET auth/setup — the server sends `{ required: true }` (+ `open`) while no account exists, 404 afterwards. */
export interface SetupState {
  required?: boolean;
  open?: boolean;
}

export interface SetupPayload {
  token: string;
  username: string;
  password: string;
  display_name: string;
}

/** `GET auth/invite-class?code=` — class behind a student invite code. */
export interface InviteClassPreview {
  /** false when the code is unknown or disabled — the server never says which */
  valid: boolean;
  /** present only when `valid` */
  class_name?: string;
}

/** The invitation itself, as stored by the server. */
export interface Invitation {
  code: string;
  roles: Role[];
  class_id?: string | null;
  class_name?: string | null;
  /** ISO timestamp; 72 h from issue (ACCOUNTS.md §2). */
  expires_at?: string | null;
  /** E1_phonetics / E2_vocal / E3_extra */
  annotator_track?: string | null;
  note?: string | null;
  invited_by?: string | null;
}

/** `GET auth/invite/:code` — the invitation comes wrapped. */
export interface InvitePreview {
  invitation: Invitation;
}

export interface AcceptInvitePayload {
  username: string;
  password: string;
  display_name: string;
  /** 《标注成果授权协议》 signed at acceptance; ignored by older servers. */
  work_agreement?: { version: string; accepted: true };
}

/** Recorded when a staff account signed the work agreement. */
export interface WorkAgreementStatus {
  version: string;
  accepted_at?: string | null;
}

/** `GET account/sessions` — ACCOUNTS.md §3 「我的设备」. */
export interface DeviceSession {
  /**
   * Display id. For any device other than the current one the server truncates
   * it (`"a1b2c3d4…"`) — revoking must use {@link DeviceSession.revoke_id}.
   */
  id: string;
  /** The full session id, the only value `DELETE account/sessions/:id` accepts. */
  revoke_id: string;
  user_agent: string | null;
  created_at: string | null;
  /** Server field is `last_seen_at`; normalised by {@link adaptSession}. */
  last_active_at: string | null;
  /** true for the session making the request */
  current: boolean;
  ip?: string | null;
  remember?: boolean;
  expires_at?: string | null;
}

/** Raw `account/sessions` row → {@link DeviceSession}. */
function adaptSession(raw: Record<string, unknown>): DeviceSession {
  const id = String(raw.id ?? "");
  return {
    ...(raw as unknown as DeviceSession),
    id,
    revoke_id: typeof raw.revoke_id === "string" && raw.revoke_id ? raw.revoke_id : id,
    last_active_at:
      (raw.last_active_at as string | null | undefined) ??
      (raw.last_seen_at as string | null | undefined) ??
      null,
  };
}

export const accountApi = {
  changePassword: (currentPassword: string, newPassword: string) =>
    apiFetch<{ ok: true }>("/account/password", {
      method: "POST",
      // `passwordChangeSchema` names these `current` / `next`.
      body: { current: currentPassword, next: newPassword },
    }),
  sessions: async (): Promise<{ sessions: DeviceSession[] }> => {
    const r = await apiFetch<{ sessions: Record<string, unknown>[] }>("/account/sessions");
    return { sessions: (r.sessions ?? []).map(adaptSession) };
  },
  /** Pass `DeviceSession.revoke_id`; the displayed `id` is truncated. */
  revokeSession: (revokeId: string) =>
    apiFetch<{ ok: true; current?: boolean }>(`/account/sessions/${encodeURIComponent(revokeId)}`, {
      method: "DELETE",
    }),
  /** server README: 注销其他全部 is `DELETE account/sessions`, not a POST sub-path. */
  revokeOtherSessions: () =>
    apiFetch<{ ok: true; revoked: number }>("/account/sessions", { method: "DELETE" }),
};

/**
 * ACCOUNTS.md §4 — the optional, verified contact address.
 * Every call 409s with `SMTP_NOT_CONFIGURED` when the deployment has no SMTP,
 * and `GET account/email` says so in `smtp` so the page can hide the section.
 */
export interface AccountEmailState {
  /** false when this deployment has no SMTP at all. */
  smtp: boolean;
  /** The verified address, or null. */
  email: string | null;
  verified_at: string | null;
  /** An address waiting for its 6-digit code. */
  pending: { email: string; expires_at: string } | null;
}

export const accountEmailApi = {
  get: (signal?: AbortSignal) =>
    apiFetch<AccountEmailState>("/account/email", signal ? { signal } : {}),
  /** Sends a 6-digit code to `email`. */
  start: (email: string) =>
    apiFetch<AccountEmailState>("/account/email", { method: "POST", body: { email } }),
  verify: (code: string) =>
    apiFetch<AccountEmailState>("/account/email/verify", { method: "POST", body: { code } }),
  remove: () => apiFetch<AccountEmailState>("/account/email", { method: "DELETE" }),
};

/** ACCOUNTS.md §4 — verified mainland mobile, used for SMS password recovery. */
export interface AccountPhoneState {
  /** false when this deployment has no SMS credentials. */
  sms: boolean;
  phone: string | null;
  verified_at: string | null;
  pending: { phone: string; expires_at: string } | null;
}

export const accountPhoneApi = {
  get: (signal?: AbortSignal) =>
    apiFetch<AccountPhoneState>("/account/phone", signal ? { signal } : {}),
  start: (phone: string) =>
    apiFetch<AccountPhoneState>("/account/phone", { method: "POST", body: { phone } }),
  verify: (code: string) =>
    apiFetch<AccountPhoneState>("/account/phone/verify", { method: "POST", body: { code } }),
  remove: () => apiFetch<AccountPhoneState>("/account/phone", { method: "DELETE" }),
};

/** Display name and username. Does not revoke sessions. */
export const accountProfileApi = {
  update: (patch: { display_name?: string; username?: string }) =>
    apiFetch<{ user: { id: string; username: string; display_name: string } }>("/account/profile", {
      method: "PATCH",
      body: patch,
    }),
};

/** `GET health` — public deployment facts the login screen needs. */
export interface HealthResponse {
  ok: boolean;
  version?: string;
  storage?: { kind: string; bucket?: string };
  /** `forgot_password` is true only when SMTP is configured. `ai_v1` defaults off. */
  features?: { forgot_password?: boolean; sms_recovery?: boolean; ai_v1?: boolean };
}

export const adminAiApi = {
  status: (signal?: AbortSignal) =>
    apiFetch<{ ai: AiAdminStatus }>("/admin/ai", signal ? { signal } : {}),
  retry: (recordingId: string) =>
    apiFetch<{ ai: AiPublicResult }>(`/admin/ai/${encodeURIComponent(recordingId)}/retry`, {
      method: "POST",
    }),
};

export const healthApi = {
  /** Public; used by the login page to decide which 忘记密码 text to show. */
  get: (signal?: AbortSignal) => apiFetch<HealthResponse>("/health", signal ? { signal } : {}),
};

export const authApi = {
  register: (payload: RegisterPayload) =>
    fetchMe("/auth/register", { method: "POST", body: payload }),
  /** `remember` extends the session to 30 days (ACCOUNTS.md §3). */
  login: (username: string, password: string, remember = false) =>
    fetchMe("/auth/login", {
      method: "POST",
      body: { username, password, remember },
    }),
  /** ACCOUNTS.md §1 — first-admin bootstrap; 404 / SETUP_CLOSED once used. */
  setupState: () => apiFetch<SetupState>("/auth/setup"),
  setup: (payload: SetupPayload) => fetchMe("/auth/setup", { method: "POST", body: payload }),
  /**
   * Preview of a class invite code used by students on `/register`.
   * Optional on the server — the form falls back to validating on submit.
   */
  inviteClass: (code: string) =>
    apiFetch<InviteClassPreview>("/auth/invite-class", { query: { code } }),
  /** ACCOUNTS.md §2 — invite link preview and acceptance. */
  invitePreview: (token: string) =>
    apiFetch<InvitePreview>(`/auth/invite/${encodeURIComponent(token)}`),
  acceptInvite: (token: string, payload: AcceptInvitePayload) =>
    fetchMe(`/auth/invite/${encodeURIComponent(token)}`, {
      method: "POST",
      body: payload,
    }),
  /** Logged-in redeem: add this invitation's roles to the current account. */
  joinInvite: (token: string, payload: { work_agreement?: { version: string; accepted: true } } = {}) =>
    fetchMe(`/auth/invite/${encodeURIComponent(token)}/join`, {
      method: "POST",
      body: payload,
    }),
  /**
   * ACCOUNTS.md §4 忘记密码自助 — **always** resolves with the same generic
   * body, whatever the account state is. The page must not try to infer
   * anything from it beyond `smtp`.
   */
  forgot: (account: string) =>
    apiFetch<{ ok: true; message: string; smtp: boolean; sms?: boolean }>("/auth/forgot", {
      method: "POST",
      body: { account },
    }),
  recoverSms: (account: string, code: string, password: string) =>
    apiFetch<{ ok: true }>("/auth/recover/sms", {
      method: "POST",
      body: { account, code, password },
    }),
  /** ACCOUNTS.md §4 — one-time reset link. */
  resetPassword: (token: string, password: string) =>
    apiFetch<{ ok: true }>(`/auth/reset/${encodeURIComponent(token)}`, {
      method: "POST",
      body: { password },
    }),
  logout: () => apiFetch<{ ok: true }>("/auth/logout", { method: "POST" }),
  me: (signal?: AbortSignal) => fetchMe("/auth/me", signal ? { signal } : {}),
  consent: (payload: Consent & { minor_guardian_signed?: boolean }) =>
    fetchMe("/auth/consent", {
      method: "POST",
      body: {
        train: payload.train,
        display: payload.display,
        commercial: payload.commercial,
        ...(payload.minor_guardian_signed !== undefined
          ? { minor_guardian_signed: payload.minor_guardian_signed }
          : {}),
      },
    }),
  // No self-service withdrawal: 撤回授权 is a written request to 叽喳科技,
  // recorded by the course team (`consent_withdrawn_at` stays server-side).
};

// --------------------------------------------------------------------------- //
// student
// --------------------------------------------------------------------------- //

export const studentApi = {
  assignments: (signal?: AbortSignal) =>
    apiFetch<AssignmentsResponse>("/me/assignments", signal ? { signal } : {}),
  profile: (signal?: AbortSignal) =>
    apiFetch<ProfileResponse>("/me/profile", signal ? { signal } : {}),
  updateProfile: (patch: Partial<ProfilePayload>) =>
    apiFetch<ProfileResponse>("/me/profile", { method: "PATCH", body: patch }),
  recordings: (signal?: AbortSignal) =>
    apiFetch<RecordingsResponse>("/me/recordings", signal ? { signal } : {}),
  /** Persists 试录 so another browser, and view-as, will not open it again. */
  finishWarmup: () => apiFetch<{ warmup_done: boolean }>("/me/warmup", { method: "POST", body: {} }),
  /** Assignment-level skip with a reason (docs/UX.md 2 「跳过并说明原因」). */
  skip: (assignmentId: string, reason: string, note?: string) =>
    apiFetch<{ assignment: AssignmentItem }>(
      `/me/assignments/${encodeURIComponent(assignmentId)}/skip`,
      { method: "POST", body: { reason, note: note ?? null } },
    ),
  perceptionTrials: (
    assignmentId: string,
    trials: Array<Omit<PerceptionTrial, "speaker_id" | "id">>,
    sessionId: string,
  ) =>
    apiFetch<{ saved: number }>("/perception/trials", {
      method: "POST",
      body: { assignment_id: assignmentId, session_id: sessionId, trials },
    }),
};

/** `GET recordings/:id/audio` — used as an `<audio src>`; auth is the cookie. */
export function recordingAudioUrl(recordingId: string): string {
  return `${API_BASE}/recordings/${encodeURIComponent(recordingId)}/audio`;
}

/**
 * Multipart upload with progress. `fetch` cannot report upload progress, so
 * this uses XHR; the caller keeps the blob so a failure can be retried.
 */
export function uploadRecording(
  input: UploadRecordingInput,
  onProgress?: (fraction: number) => void,
): Promise<UploadRecordingResponse> {
  const form = new FormData();
  form.set("audio", input.blob, input.filename ?? "recording.wav");
  form.set("assignment_id", input.assignment_id);
  form.set("session_id", input.session_id);
  form.set("elicitation", input.elicitation ?? "natural");
  form.set("duration_ms", String(Math.round(input.duration_ms)));
  form.set("sample_rate", String(Math.round(input.sample_rate)));
  form.set("client_meta", JSON.stringify(input.client_meta));
  if (input.paired_with) form.set("paired_with", input.paired_with);
  if (input.retest_of) form.set("retest_of", input.retest_of);
  // B2 — same key on the field and the header, so a proxy that drops one still
  // lets the server dedupe.
  if (input.upload_key) form.set("upload_key", input.upload_key);

  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("POST", `${API_BASE}/recordings`, true);
    xhr.withCredentials = true;
    xhr.responseType = "text";
    if (input.upload_key) xhr.setRequestHeader("X-Upload-Key", input.upload_key);
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable && e.total > 0) onProgress?.(e.loaded / e.total);
    };
    xhr.onerror = () => reject(new ApiError(0, { code: "NETWORK", message: FALLBACK_MESSAGE[0]! }));
    xhr.onabort = () => reject(new ApiError(0, { code: "ABORTED", message: "上传已取消。" }));
    xhr.onload = () => {
      let payload: unknown = null;
      try {
        payload = xhr.responseText ? JSON.parse(xhr.responseText) : null;
      } catch {
        payload = null;
      }
      if (xhr.status >= 200 && xhr.status < 300) {
        onProgress?.(1);
        resolve(payload as UploadRecordingResponse);
      } else {
        reject(new ApiError(xhr.status, toErrorBody(xhr.status, payload)));
      }
    };
    xhr.send(form);
  });
}

// --------------------------------------------------------------------------- //
// stubs for the screens other agents own — transport only, no UI assumptions
// --------------------------------------------------------------------------- //

export const annotateApi = {
  /** `GET annotate/queue` — the server names the batch filter `batch`. */
  queue: (params: AnnotateQueueParams = {}, signal?: AbortSignal) =>
    apiFetch<AnnotateQueueResponse>("/annotate/queue", {
      query: {
        batch: params.batch_id ?? null,
        task: params.task ?? null,
        domain: params.domain ?? null,
        limit: params.limit ?? null,
      },
      ...(signal ? { signal } : {}),
    }),
  /** `GET annotate/:recordingId` — material + **only my own** judgment. */
  get: (recordingId: string, signal?: AbortSignal) =>
    apiFetch<AnnotateDetail>(
      `/annotate/${encodeURIComponent(recordingId)}`,
      signal ? { signal } : {},
    ),
  /** `POST annotate/:recordingId` — body validated with `judgmentSubmitSchema`. */
  submit: (recordingId: string, judgment: JudgmentSubmitPayload) =>
    apiFetch<AnnotateSubmitResponse>(`/annotate/${encodeURIComponent(recordingId)}`, {
      method: "POST",
      body: judgment,
    }),
  /**
   * `DELETE annotate/:recordingId` — 撤回 the judgment just submitted
   * (docs/UX.md §2.2; the server allows it for 5 minutes). A 409 means the
   * window closed or the merge already ran.
   */
  retract: (recordingId: string) =>
    apiFetch<{ ok: true }>(`/annotate/${encodeURIComponent(recordingId)}`, { method: "DELETE" }),
  /**
   * `GET annotate/rereview` — §5.4 「讨论」档 (0.10 < Δ ≤ 0.25).
   * The peer's value is disclosed for these dimensions only, never with a name.
   */
  rereview: (signal?: AbortSignal) =>
    apiFetch<RereviewResponse>("/annotate/rereview", signal ? { signal } : {}),
  /**
   * `POST annotate/rereview/:id` — confirm or change my own value.
   * `services/rereview.ts` answers a `RereviewSubmitResult`; the page only
   * needs "did it land" + "how many are left on this recording".
   */
  confirmRereview: async (
    itemId: string,
    value: number | string | null,
  ): Promise<RereviewConfirmResponse> => {
    const r = await apiFetch<{
      id: string;
      done: boolean;
      recomputed: boolean;
      resolution: string | null;
      delta: number | null;
      needs_arbitration: number;
      pending_for_recording: number;
    }>(`/annotate/rereview/${encodeURIComponent(itemId)}`, {
      method: "POST",
      body: { value },
    });
    return { ...r, ok: r.done !== false, remaining: r.pending_for_recording ?? 0 };
  },
};

/** 黄金题 (§1.5) — course_team attaches a reference answer to a recording. */
export const goldenApi = {
  /** Server answers `{recording, golden, golden_answer}`; flatten to `{recording_id, golden}`. */
  set: async (recordingId: string, answer: GoldenPayload["answer"]): Promise<GoldenResponse> => {
    const r = await apiFetch<{ recording?: { id?: string }; golden: boolean }>(
      `/recordings/${encodeURIComponent(recordingId)}/golden`,
      { method: "POST", body: { answer } },
    );
    return { recording_id: r.recording?.id ?? recordingId, golden: !!r.golden };
  },
};

export const arbitrationApi = {
  queue: (signal?: AbortSignal) =>
    apiFetch<ArbitrationQueueResponse>("/arbitration/queue", signal ? { signal } : {}),
  get: (recordingId: string, signal?: AbortSignal) =>
    apiFetch<ArbitrationDetail>(
      `/arbitration/${encodeURIComponent(recordingId)}`,
      signal ? { signal } : {},
    ),
  /** Utterance-level dimensions use the `"u"` path segment (server contract). */
  decide: (
    recordingId: string,
    charIndex: number | null,
    dim: string,
    decision: ArbitrationDecidePayload,
  ) =>
    apiFetch<ArbitrationDecideResponse>(
      `/arbitration/${encodeURIComponent(recordingId)}/${charIndex === null ? "u" : charIndex}/${encodeURIComponent(dim)}`,
      { method: "POST", body: decision },
    ),
};

export const qualityApi = {
  /** Generic so 质量看板 can pass its own `QualitySummaryResponse`. */
  summary: <T = unknown>(signal?: AbortSignal) =>
    apiFetch<T>("/quality/summary", signal ? { signal } : {}),
  /** An annotator only ever gets their own row back (AUTHZ.md). */
  annotators: <T = QualityAnnotatorsResponse>(signal?: AbortSignal) =>
    apiFetch<T>("/quality/annotators", signal ? { signal } : {}),
  /**
   * docs/ROADMAP.md B5 —— 周度 IAA (ISO 周 · Asia/Shanghai)。
   * 标注专家只拿到自己那几行，配对里对方是「另一位专家」（双盲不开口子）。
   */
  weekly: (params: { weeks?: number } = {}, signal?: AbortSignal) =>
    apiFetch<QualityWeeklyResponse>("/quality/weekly", {
      query: params.weeks ? { weeks: params.weeks } : {},
      ...(signal ? { signal } : {}),
    }),
};

// --------------------------------------------------------------------------- //
// 课程团队 / 管理端 (docs/UX.md 11–18) — shapes in features/admin/types.ts
// --------------------------------------------------------------------------- //

export const materialsApi = {
  list: (
    params: {
      task?: Task;
      domain?: Domain;
      kind?: string;
      /** `corpus_v1` | `ai_test_v1` */
      collection?: string;
      /** 《AI 正音测试》level 1–3 (only meaningful with `collection`) */
      level?: number;
    } = {},
    signal?: AbortSignal,
  ) =>
    apiFetch<MaterialListResponse>("/materials", { query: params, ...(signal ? { signal } : {}) }),
  get: (id: string) => apiFetch<MaterialDetailResponse>(`/materials/${encodeURIComponent(id)}`),
  /** Body is a `materialImportSchema` payload (红线 14.5 re-checked server side). */
  import: (payload: MaterialImportPayload) =>
    apiFetch<MaterialImportResponse>("/materials/import", { method: "POST", body: payload }),
  freeze: (id: string, version: string) =>
    apiFetch<MaterialFreezeResponse>(`/materials/${encodeURIComponent(id)}/freeze`, {
      method: "POST",
      body: { version },
    }),
};

export const speakersApi = {
  /** `verified: 0` narrows to 待核验 (ACCOUNTS.md §6). */
  list: (params: { verified?: 0 | 1 } = {}, signal?: AbortSignal) =>
    apiFetch<SpeakerListResponse>("/speakers", {
      query: params.verified === undefined ? {} : { verified: params.verified },
      ...(signal ? { signal } : {}),
    }),
  /** Body is a `speakerImportSchema` payload. */
  import: (payload: SpeakerImportPayload) =>
    apiFetch<SpeakerImportResponse>("/speakers/import", { method: "POST", body: payload }),
  update: (id: string, patch: SpeakerUpdatePayload & { verified?: boolean }) =>
    apiFetch<SpeakerUpdateResponse>(`/speakers/${encodeURIComponent(id)}`, {
      method: "PATCH",
      body: patch,
    }),
  /** ACCOUNTS.md §6 — course-team verification of a self-registered student. */
  verify: (id: string, patch: { class_id?: string | null } = {}) =>
    apiFetch<SpeakerUpdateResponse>(`/speakers/${encodeURIComponent(id)}`, {
      method: "PATCH",
      body: { verified: true, ...patch },
    }),
  /** Read-only 回看: recordings plus expert annotations for one student. */
  recordings: (id: string, signal?: AbortSignal) =>
    apiFetch<SpeakerRecordingsResponse>(`/speakers/${encodeURIComponent(id)}/recordings`, {
      ...(signal ? { signal } : {}),
    }),
};

/**
 * Raw `classes.ts payload()` row → {@link ClassItem}.
 * The server names the two counts `speakers` / `unverified`.
 */
function adaptClass(raw: Record<string, unknown>): ClassItem {
  return {
    ...(raw as unknown as ClassItem),
    student_count:
      (raw.student_count as number | null | undefined) ?? (raw.speakers as number | undefined) ?? 0,
    unverified_count:
      (raw.unverified_count as number | null | undefined) ??
      (raw.unverified as number | undefined) ??
      0,
  };
}

/** `classCreateSchema` requires an id; derive a stable one from the name. */
function classIdFrom(name: string): string {
  const slug = name
    .trim()
    .toLowerCase()
    .replace(/[^\p{L}\p{N}]+/gu, "-")
    .replace(/^-+|-+$/g, "");
  return slug.length > 0 ? slug.slice(0, 64) : `class-${Date.now().toString(36)}`;
}

/** 班级 + 班级邀请码 (ACCOUNTS.md §2 学生入口). */
export const classesApi = {
  list: async (signal?: AbortSignal): Promise<ClassListResponse> => {
    const r = await apiFetch<{ classes: Record<string, unknown>[] }>(
      "/classes",
      signal ? { signal } : {},
    );
    return { classes: (r.classes ?? []).map(adaptClass) };
  },
  create: async (payload: { id?: string; name: string }): Promise<ClassResponse> => {
    const r = await apiFetch<{ class: Record<string, unknown> }>("/classes", {
      method: "POST",
      body: { id: payload.id?.trim() || classIdFrom(payload.name), name: payload.name },
    });
    return { class: adaptClass(r.class) };
  },
  /**
   * Rotates the invite code; pass `invite_enabled` to stop / resume sign-ups.
   * `classInviteSchema` names the switch `enabled` and refuses an empty body,
   * so an enable/disable-only patch must not also ask for a rotation.
   */
  rotateInvite: async (
    id: string,
    patch: { invite_enabled?: boolean } = {},
  ): Promise<ClassResponse> => {
    const body =
      patch.invite_enabled === undefined
        ? { rotate: true }
        : { enabled: patch.invite_enabled, rotate: false };
    const r = await apiFetch<{ class: Record<string, unknown> }>(
      `/classes/${encodeURIComponent(id)}/invite`,
      { method: "POST", body },
    );
    return { class: adaptClass(r.class) };
  },
  members: (id: string, signal?: AbortSignal) =>
    apiFetch<ClassMemberListResponse>(
      `/classes/${encodeURIComponent(id)}/members`,
      signal ? { signal } : {},
    ),
  setMemberRoles: (id: string, userId: string, roles: string[]) =>
    apiFetch<ClassMemberListResponse>(
      `/classes/${encodeURIComponent(id)}/members/${encodeURIComponent(userId)}`,
      { method: "PATCH", body: { roles } },
    ),
  removeMember: (id: string, userId: string) =>
    apiFetch<ClassMemberListResponse & { ok: true }>(
      `/classes/${encodeURIComponent(id)}/members/${encodeURIComponent(userId)}`,
      { method: "DELETE" },
    ),
  invitations: (id: string, signal?: AbortSignal) =>
    apiFetch<InvitationListResponse>(
      `/classes/${encodeURIComponent(id)}/invitations`,
      signal ? { signal } : {},
    ),
  createInvitation: (
    id: string,
    payload: {
      roles: string[];
      annotator_track?: string | null;
      label?: string | null;
      max_uses?: number | null;
      expires_in_hours?: number;
    },
  ) =>
    apiFetch<{ invitation: AdminInvitation }>(`/classes/${encodeURIComponent(id)}/invitations`, {
      method: "POST",
      body: payload,
    }),
  /** Teachers revoke here; `/admin/invitations` stays admin-only. */
  revokeInvitation: (token: string) =>
    apiFetch<{ ok: true }>(`/invitations/${encodeURIComponent(token)}`, { method: "DELETE" }),
};

export const batchesApi = {
  list: (signal?: AbortSignal) => apiFetch<BatchListResponse>("/batches", signal ? { signal } : {}),
  get: (id: string) => apiFetch<BatchDetailResponse>(`/batches/${encodeURIComponent(id)}`),
  /** Per-student status and recording preview for a published task. */
  students: (id: string, signal?: AbortSignal) =>
    apiFetch<BatchStudentsResponse>(`/batches/${encodeURIComponent(id)}/students`, {
      ...(signal ? { signal } : {}),
    }),
  /** Body is a `batchCreateSchema` payload. */
  create: (payload: BatchCreatePayload) =>
    apiFetch<BatchResponse>("/batches", { method: "POST", body: payload }),
  publish: (id: string) =>
    apiFetch<BatchResponse>(`/batches/${encodeURIComponent(id)}/publish`, {
      method: "POST",
      body: { confirm: true },
    }),
  /** Body is a `batchDispatchSchema` payload (retest_round / allow_duplicate included). */
  dispatch: (id: string, payload: BatchDispatchPayload) =>
    apiFetch<BatchDispatchResponse>(`/batches/${encodeURIComponent(id)}/dispatch`, {
      method: "POST",
      body: payload,
    }),

  /**
   * docs/ROADMAP.md B4 —— 把批次里已质检的录音配给标注专家。
   * `dry_run: true` 只算预览（每位专家的负载），一行都不会写；
   * 「分配标注」按钮就是「预览 → 确认」两步。
   */
  assignAnnotators: (id: string, payload: AnnotationAssignPayload) =>
    apiFetch<AssignAnnotatorsResponse>(`/batches/${encodeURIComponent(id)}/assign-annotators`, {
      method: "POST",
      body: payload,
    }),

  /** 撤销分配；已提交判断的那个人保留（双盲记录不可撤）。 */
  unassignAnnotators: (id: string, payload: AnnotationUnassignPayload) =>
    apiFetch<UnassignAnnotatorsResponse>(`/batches/${encodeURIComponent(id)}/unassign-annotators`, {
      method: "POST",
      body: payload,
    }),

  /**
   * docs/ROADMAP.md C1 —— 从基线批次生成第 1 / 第 2 次复测（同一批学生、
   * 同一批素材、同样的设置）。`dry_run: true` 只说「将会创建什么」。
   */
  retest: (id: string, payload: RetestCreatePayload) =>
    apiFetch<RetestResponse>(`/batches/${encodeURIComponent(id)}/retest`, {
      method: "POST",
      body: payload,
    }),
};

export const qcApi = {
  /**
   * 质检队列 — recordings still sitting in `uploaded`.
   *
   * `filter` is the C3 热图钻取：一个学生 + 一个环节，可选地不限状态。
   * The teacher scope is enforced server-side; these only ever narrow it.
   */
  list: (
    state = "uploaded",
    signal?: AbortSignal,
    filter: {
      speaker_id?: string;
      task?: string;
      all_states?: boolean;
      limit?: number;
      offset?: number;
    } = {},
  ) =>
    apiFetch<QcListResponse>("/recordings", {
      query: {
        state,
        ...(filter.speaker_id ? { speaker_id: filter.speaker_id } : {}),
        ...(filter.task ? { task: filter.task } : {}),
        ...(filter.all_states ? { all_states: "1" } : {}),
        ...(filter.limit != null ? { limit: filter.limit } : {}),
        ...(filter.offset != null && filter.offset > 0 ? { offset: filter.offset } : {}),
      },
      ...(signal ? { signal } : {}),
    }),
  /** Body is a `recordingQcSchema` payload; 驳回 requires a note. */
  decide: (recordingId: string, payload: RecordingQcPayload) =>
    apiFetch<QcDecisionResponse>(`/recordings/${encodeURIComponent(recordingId)}/qc`, {
      method: "POST",
      body: payload,
    }),
  /**
   * docs/UX.md §3.2 「通过全部 SNR 良好」 — one call instead of N.
   *
   * `bulkQcSchema` only accepts `{recording_ids, decision:'qc_ok', note?}` and
   * answers `{ok, approved, skipped}`; the caller's `min_snr_db` filter is
   * applied client-side when building the id list.
   */
  bulk: async (payload: {
    decision: "qc_ok";
    recording_ids: string[];
    min_snr_db?: number;
    note?: string | null;
  }): Promise<QcBulkResponse> => {
    const r = await apiFetch<{
      ok: number;
      approved: string[];
      skipped: Array<{ id: string; reason: string }>;
    }>("/recordings/qc/bulk", {
      method: "POST",
      body: {
        decision: "qc_ok",
        recording_ids: payload.recording_ids,
        ...(payload.note === undefined ? {} : { note: payload.note }),
      },
    });
    return {
      updated: r.ok ?? 0,
      recording_ids: r.approved ?? [],
      skipped: r.skipped ?? [],
    };
  },
  recording: (recordingId: string) =>
    apiFetch<{ recording: Recording }>(`/recordings/${encodeURIComponent(recordingId)}`),
};

/**
 * 示范录音 — T7 听辨题的参考音 (`routes/stimuli.ts`).
 *
 * 红线：这些音**必须是 杨爽老师 本人录的**，平台不提供也不接受任何 TTS / 合成音。
 * 上传走 multipart，和学生端录音上传同一条路子。
 */
export const stimuliApi = {
  list: (signal?: AbortSignal) =>
    apiFetch<StimuliListResponse>("/stimuli", signal ? { signal } : {}),

  /** Replace-or-create one take. `blob` is a 48 kHz 16-bit mono WAV. */
  upload: async (syllable: string, take: number, blob: Blob, filename?: string) => {
    const form = new FormData();
    form.set("audio", blob, filename ?? `${take}.wav`);
    const res = await fetch(`${API_BASE}/stimuli/${encodeURIComponent(syllable)}/${take}`, {
      method: "POST",
      credentials: "include",
      body: form,
    }).catch((cause: unknown) => {
      throw new ApiError(0, toErrorBody(0, { code: "NETWORK", details: String(cause) }));
    });
    const text = await res.text();
    let payload: unknown = null;
    try {
      payload = text ? JSON.parse(text) : null;
    } catch {
      payload = null;
    }
    if (!res.ok) throw new ApiError(res.status, toErrorBody(res.status, payload));
    return payload as StimulusUploadResponse;
  },

  remove: (syllable: string, take: number) =>
    apiFetch<{ deleted: boolean }>(`/stimuli/${encodeURIComponent(syllable)}/${take}`, {
      method: "DELETE",
    }),

  /** `GET stimuli/:syllable/:take/audio` — used as an `<audio src>`. */
  audioUrl: (syllable: string, take: number) =>
    `${API_BASE}/stimuli/${encodeURIComponent(syllable)}/${take}/audio`,
};

/** docs/UX.md §3.1 班级看板 — one call for the whole teacher home screen. */
export const teacherApi = {
  dashboard: (params: { class_id?: string } = {}, signal?: AbortSignal) =>
    apiFetch<TeacherDashboardResponse>("/teacher/dashboard", {
      query: params.class_id ? { class_id: params.class_id } : {},
      ...(signal ? { signal } : {}),
    }),
  /**
   * C3 班级覆盖热图 — one aggregate for the whole grid (docs/UX.md §3.3).
   * `by` picks the column axis: 采集环节 or 混淆类.
   */
  coverage: (params: { class_id?: string; by?: CoverageAxis } = {}, signal?: AbortSignal) =>
    apiFetch<CoverageResponse>("/teacher/coverage", {
      query: {
        ...(params.class_id ? { class_id: params.class_id } : {}),
        ...(params.by ? { by: params.by } : {}),
      },
      ...(signal ? { signal } : {}),
    }),
};

export const exportApi = {
  /** Body is an `exportPreviewSchema` payload. */
  preview: (payload: ExportPreviewPayload) =>
    apiFetch<ExportPreviewResponse>("/export/preview", { method: "POST", body: payload }),
  /** Body is an `exportFreezeSchema` payload (+ optional `batch_ids`). */
  freeze: (payload: ExportFreezePayload & { batch_ids?: string[] }) =>
    apiFetch<ExportFreezeResponse>("/export/freeze", { method: "POST", body: payload }),
  list: (signal?: AbortSignal) => apiFetch<ExportListResponse>("/export", signal ? { signal } : {}),
  downloadUrl: (version: string, file: "manifest" | "product_materials" = "manifest") =>
    `${API_BASE}/export/${encodeURIComponent(version)}/${file}.jsonl`,
  manifestUrl: (version: string) =>
    `${API_BASE}/export/${encodeURIComponent(version)}/manifest.jsonl`,
};

export interface UpdateUserInput {
  display_name?: string;
  roles?: Role[];
  disabled?: boolean;
  /** annotator 方向 (ACCOUNTS.md §5). */
  track?: AnnotatorTrack | null;
  /** Full class-membership set. Omitted leaves memberships untouched. */
  memberships?: Array<{ class_id: string; roles: Role[] }>;
}

/**
 * admin only (AUTHZ.md 账号管理).
 *
 * ACCOUNTS.md §1: there is no "create with a password" path any more — an admin
 * either issues an invitation link or a one-time reset link, and never sees or
 * sets a plaintext password.
 */
/** Raw `admin.ts detail()` row → {@link AdminUser} (`sessions` → `session_count`). */
function adaptAdminUser(raw: Record<string, unknown>): AdminUser {
  return {
    ...(raw as unknown as AdminUser),
    session_count:
      (raw.session_count as number | null | undefined) ?? (raw.sessions as number | undefined) ?? 0,
  };
}

export const usersApi = {
  list: async (signal?: AbortSignal): Promise<AdminUserListResponse> => {
    const r = await apiFetch<{ users: Record<string, unknown>[] }>(
      "/admin/users",
      signal ? { signal } : {},
    );
    return { users: (r.users ?? []).map(adaptAdminUser) };
  },
  update: async (id: string, patch: UpdateUserInput): Promise<AdminUserResponse> => {
    // ACCOUNTS.md §5 — the server names the 标注者方向 field `annotator_track`.
    const { track, ...rest } = patch;
    const r = await apiFetch<{ user: Record<string, unknown> }>(
      `/admin/users/${encodeURIComponent(id)}`,
      {
        method: "PATCH",
        body: { ...rest, ...(track === undefined ? {} : { annotator_track: track }) },
      },
    );
    return { user: adaptAdminUser(r.user) };
  },
  /**
   * Admin reads the product as that student. The cookie stays the admin's.
   * `POST /api/v1/admin/users/:id/view-as`
   */
  viewAs: async (
    id: string,
  ): Promise<{
    viewing_as: { user_id: string; display_name: string; started_at: string; expires_at: string };
  }> =>
    apiFetch(`/admin/users/${encodeURIComponent(id)}/view-as`, { method: "POST", body: {} }),
  resetLink: async (id: string): Promise<ResetLinkResponse> => {
    const r = await apiFetch<
      ResetLinkResponse & { reset_link?: ResetLinkResponse & { token?: string; username?: string } }
    >(`/admin/users/${encodeURIComponent(id)}/reset-link`, { method: "POST", body: {} });
    return r.reset_link ?? r;
  },
  /** Leave view-as. `POST /api/v1/admin/view-as/end` */
  endViewAs: async (): Promise<{ ok: boolean }> =>
    apiFetch("/admin/view-as/end", { method: "POST", body: {} }),
};

export interface CreateInvitationInput {
  roles: Role[];
  /** Ties a teacher / student invitation to a class. */
  class_id?: string | null;
  annotator_track?: AnnotatorTrack | null;
  /** ACCOUNTS.md §2 defaults to 72 hours. */
  expires_hours: number;
  note?: string | null;
  label?: string | null;
  /** `null` = unlimited. Omit to keep the admin default of one use. */
  max_uses?: number | null;
}

/**
 * Raw `admin.ts inviteBody()` row → {@link AdminInvitation}.
 * The server only sends the short `code`, so mirror it into `token`; a revoked
 * invitation is deleted rather than flagged, so `revoked_at` never arrives.
 */
function adaptInvitation(raw: Record<string, unknown>): AdminInvitation {
  const code = String(raw.code ?? raw.token ?? "");
  return {
    ...(raw as unknown as AdminInvitation),
    code,
    token: typeof raw.token === "string" && raw.token ? raw.token : code,
  };
}

/** 邀请链接 (ACCOUNTS.md §2) — one-time, role-preset, no email required. */
export const invitationsApi = {
  list: async (signal?: AbortSignal): Promise<InvitationListResponse> => {
    const r = await apiFetch<{ invitations: Record<string, unknown>[] }>(
      "/admin/invitations",
      signal ? { signal } : {},
    );
    return { invitations: (r.invitations ?? []).map(adaptInvitation) };
  },
  /** Server wraps the created row as `{ invitation }`; unwrap so callers get the row. */
  create: async (payload: CreateInvitationInput): Promise<InvitationCreateResponse> => {
    const { expires_hours, ...rest } = payload;
    const r = await apiFetch<Record<string, unknown> & { invitation?: Record<string, unknown> }>(
      "/admin/invitations",
      {
        method: "POST",
        // `invitationCreateSchema` names the TTL `expires_in_hours`.
        body: {
          ...rest,
          ...(expires_hours ? { expires_in_hours: expires_hours } : {}),
          ...(payload.label ? { label: payload.label } : {}),
          ...(payload.max_uses !== undefined ? { max_uses: payload.max_uses } : {}),
        },
      },
    );
    return adaptInvitation(r.invitation ?? r);
  },
  revoke: (token: string) =>
    apiFetch<{ ok: true }>(`/admin/invitations/${encodeURIComponent(token)}`, {
      method: "DELETE",
    }),
};

/**
 * B8 备份与深度健康检查 (docs/DEPLOY.md). Admin only; the page hides the whole
 * section for anybody else rather than showing a 403.
 */
export const opsApi = {
  backups: (signal?: AbortSignal) =>
    apiFetch<BackupListResponse>("/admin/backups", signal ? { signal } : {}),
  /** 「立即备份」 — always 200; read `result.ok` for the outcome. */
  runBackup: () => apiFetch<BackupRunResponse>("/admin/backups", { method: "POST", body: {} }),
  /** Writes to the db, probes storage and reads free disk — hence admin only. */
  healthDeep: (signal?: AbortSignal) =>
    apiFetch<DeepHealthResponse>("/health/deep", signal ? { signal } : {}),
};

/** 审计 (ACCOUNTS.md §7) — never contains passwords or judgment contents. */
export const auditApi = {
  list: (params: { user_id?: string; limit?: number } = {}, signal?: AbortSignal) =>
    apiFetch<AuditListResponse>("/admin/audit", {
      query: {
        ...(params.user_id ? { user_id: params.user_id } : {}),
        ...(params.limit ? { limit: params.limit } : {}),
      },
      ...(signal ? { signal } : {}),
    }),
};

export type { AssignmentState };
